import React, { Component } from "react";
import PropTypes from "prop-types";
import Qs from "qs";
import { Grid, List, Header, Message, Tab } from "semantic-ui-react";
import { DatePicker } from "@components/DatePicker";
import { Loader } from "@components/Loader";
import { Error } from "@components/Error";
import { BackOfficeRoutes } from "@routes/urls";
import { circulationStatsApi } from "@api/stats/circulationStats";
import { loanApi } from "@api/loans";
import { ExportSearchResults } from "@components/backoffice/ExportSearchResults";
import { invenioConfig } from "@config";
import {
  DocumentListEntry,
  DocumentStats,
} from "@modules/Document/backoffice/DocumentList";
import SearchResultsList from "@modules/SearchControls/SearchResultsList";
import RoleBasedBorrowingReport from "./RoleBasedBorrowingReport";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";

class InlineErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Message negative>
          <Message.Header>Something went wrong</Message.Header>
          <p>
            There was an error loading this component. Please try again later.
          </p>
        </Message>
      );
    }
    return this.props.children;
  }
}

InlineErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

// Enhanced DocumentStats component to include patron and usage type information
class DocumentStatsWithPatronsAndUsage extends Component {
  static propTypes = {
    metadata: PropTypes.object.isRequired,
  };

  renderUsageTypeStats = () => {
    const { metadata } = this.props;
    const usageStats = metadata.usage_stats || {};

    const {
      home_use: homeUse = 0,
      library_use: libraryUse = 0,
      not_specified: notSpecified = 0,
    } = usageStats;

    const total = homeUse + libraryUse + notSpecified;

    if (total === 0) {
      return null;
    }

    return (
      <List verticalAlign="middle" style={{ color: "#aaa" }}>
        {homeUse > 0 && (
          <List.Item>
            <List.Content floated="right">
              <Header style={{ color: "#aaa" }}>{homeUse}</Header>
            </List.Content>
            <List.Content>home use </List.Content>
          </List.Item>
        )}
        {libraryUse > 0 && (
          <List.Item>
            <List.Content floated="right">
              <Header style={{ color: "#aaa" }}>{libraryUse}</Header>
            </List.Content>
            <List.Content>library use </List.Content>
          </List.Item>
        )}
        {notSpecified > 0 && (
          <List.Item>
            <List.Content floated="right">
              <Header style={{ color: "#aaa" }}>{notSpecified}</Header>
            </List.Content>
            <List.Content>not specified</List.Content>
          </List.Item>
        )}
      </List>
    );
  };

  render() {
    const { metadata } = this.props;
    return (
      <div>
        <DocumentStats metadata={metadata} />
        {this.renderUsageTypeStats()}
      </div>
    );
  }
}

export default class MostLoanedDocumentsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fromDate: "",
      toDate: "",
      isExporting: false,
    };
  }

  componentDidMount() {
    this.fetchDocuments();
  }

  get subtitle() {
    const { fromDate, toDate } = this.state;
    const filteredHits = this.getFilteredHits();
    const total = filteredHits.length;
    let msg = `Showing ${total} most checked-out documents with patron and usage information`;

    if (fromDate && toDate) {
      return `${msg} between ${fromDate} and ${toDate}.`;
    } else if (fromDate) {
      return `${msg} starting from ${fromDate}.`;
    } else if (toDate) {
      return `${msg} ending at ${toDate}.`;
    } else {
      return `${msg}.`;
    }
  }

  fetchDocuments = () => {
    const { fromDate, toDate } = this.state;
    const { fetchMostLoanedDocuments } = this.props;
    fetchMostLoanedDocuments(fromDate, toDate);
  };

  handleFromDateChange = (value) => {
    this.setState({ fromDate: value }, this.fetchDocuments);
  };

  handleToDateChange = (value) => {
    this.setState({ toDate: value }, this.fetchDocuments);
  };

  getFilteredHits = () => {
    const { data } = this.props;

    if (!data || !data.hits) return [];

    let filteredHits = data.hits;

    return filteredHits;
  };

  createLoansUrl = (hit, fromDate, toDate) => {
    const query = loanApi
      .query()
      .withDocPid(hit.metadata.pid)
      .withStartDate({ fromDate, toDate })
      .withState(
        invenioConfig.CIRCULATION.loanActiveStates.concat(
          invenioConfig.CIRCULATION.loanCompletedStates
        )
      )
      .qs();
    return BackOfficeRoutes.loansListWithQuery(query);
  };

  handleExportClick = async (format, size) => {
    this.setState({ isExporting: true });

    try {
      if (format === "csv") {
        this.exportCustomCSV();
        return;
      }

      if (format === "pdf") {
        this.exportCustomPDF();
        return;
      }

      // For other formats, use the original API export
      const { fromDate, toDate } = this.state;
      const params = circulationStatsApi.getMostLoanedDocumentsParams(
        fromDate,
        toDate,
        size,
        format
      );
      const args = Qs.stringify(params);
      const exportUrl = `${circulationStatsApi.mostLoanedUrl}?${args}`;
      window.open(exportUrl, "_blank");
    } finally {
      setTimeout(() => {
        this.setState({ isExporting: false });
      }, 1000);
    }
  };

  exportCustomCSV = () => {
    const filteredHits = this.getFilteredHits();
    const { fromDate, toDate } = this.state;

    const top10Documents = filteredHits.slice(0, 10);

    const headers = [
      "Rank",
      "Title",
      "Authors",
      "Edition",
      "Publication Year",
      "Publisher",
      "Total Loans",
      "Unique Patrons",
      "Home Use Loans",
      "Library Use Loans",
      "Not Specified Loans",
      "Home Use",
      "Library Use",
      "Not Specified",
      "Top Patron System ID",
      "Top Patron ID",
      "Top Patron Name",
      "Top Patron Email",
      "Top Patron Role",
      "Top Patron Loans",
      "Single Borrowers",
      "Multiple Borrowers",
      "Document PID",
    ];

    const rows = top10Documents.map((hit, index) => {
      const {
        title = "",
        authors = [],
        edition = "",
        publication_year: publicationYear = "",
        imprint = {},
        loan_count: loanCount = 0,
        patron_info: patronInfo = {},
        usage_stats: usageStats = {},
        pid = "",
      } = hit.metadata;

      const authorNames = authors
        .map((author) => author.full_name || author.name || "")
        .filter((name) => name)
        .join("; ");

      const publisher = imprint?.publisher || "";
      const uniquePatrons = patronInfo.total_unique_patrons || 0;

      // Usage statistics
      const homeUse = usageStats.home_use || 0;
      const libraryUse = usageStats.library_use || 0;
      const notSpecified = usageStats.not_specified || 0;
      const homeUsePercentage = usageStats.home_use_percentage || 0;
      const libraryUsePercentage = usageStats.library_use_percentage || 0;
      const notSpecifiedPercentage = usageStats.not_specified_percentage || 0;

      // Patron information
      const topPatronSystemId = patronInfo.most_frequent_patron?.pid || "N/A";
      const topPatronId = patronInfo.most_frequent_patron?.profile_id || "N/A";
      const topPatronName = patronInfo.most_frequent_patron?.name || "N/A";
      const topPatronEmail = patronInfo.most_frequent_patron?.email || "N/A";
      const topPatronRole =
        patronInfo.most_frequent_patron?.patron_type ||
        patronInfo.most_frequent_patron?.role ||
        "N/A";
      const topPatronLoans = patronInfo.most_frequent_patron?.loan_count || 0;
      const singleBorrowers =
        patronInfo.patron_distribution?.single_borrower || 0;
      const multipleBorrowers =
        patronInfo.patron_distribution?.multiple_borrowers || 0;

      return [
        String(index + 1),
        `"${String(title).replace(/"/g, '""')}"`,
        `"${String(authorNames).replace(/"/g, '""')}"`,
        `"${String(edition).replace(/"/g, '""')}"`,
        String(publicationYear || ""),
        `"${String(publisher).replace(/"/g, '""')}"`,
        String(loanCount || 0),
        String(uniquePatrons),
        String(homeUse),
        String(libraryUse),
        String(notSpecified),
        String(homeUsePercentage),
        String(libraryUsePercentage),
        String(notSpecifiedPercentage),
        `"${String(topPatronSystemId).replace(/"/g, '""')}"`,
        `"${String(topPatronId).replace(/"/g, '""')}"`,
        `"${String(topPatronName).replace(/"/g, '""')}"`,
        `"${String(topPatronEmail).replace(/"/g, '""')}"`,
        `"${String(topPatronRole).replace(/"/g, '""')}"`,
        String(topPatronLoans),
        String(singleBorrowers),
        String(multipleBorrowers),
        String(pid || ""),
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    let filename = "top_10_most_loaned_documents_with_usage_and_patron_details";

    if (fromDate && toDate) {
      filename += `_${fromDate}_to_${toDate}`;
    } else if (fromDate) {
      filename += `_from_${fromDate}`;
    } else if (toDate) {
      filename += `_to_${toDate}`;
    }
    filename += ".csv";

    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  exportCustomPDF = () => {
    const filteredHits = this.getFilteredHits();

    const top10Documents = filteredHits.slice(0, 10);

    let reportTitle =
      "Top 10 Most Loaned Documents Report (with Usage Type & Patron Information)";

    // Generate table headers
    const tableHeaders = `
      <th>Rank</th>
      <th>Title</th>
      <th>Authors</th>
      <th>Edition</th>
      <th>Year</th>
      <th>Publisher</th>
      <th>Total Loans</th>
      <th>Home Use</th>
      <th>Library Use</th>
      <th>Not Specified Usage type</th>
      <th>Patrons</th>
      <th>Top Patron</th>
      <th>Role</th>
      <th>PID</th>
    `;

    // Generate table rows
    const tableRows = top10Documents
      .map((hit, index) => {
        const {
          title = "",
          authors = [],
          edition = "",
          publication_year: publicationYear = "",
          imprint = {},
          loan_count: loanCount = 0,
          patron_info: patronInfo = {},
          usage_stats: usageStats = {},
          pid = "",
        } = hit.metadata;

        const authorNames = authors
          .map((author) => author.full_name || author.name || "")
          .filter((name) => name)
          .join("; ");

        const publisher = imprint?.publisher || "";
        const uniquePatrons = patronInfo.total_unique_patrons || 0;

        // Usage statistics
        const homeUse = usageStats.home_use || 0;
        const libraryUse = usageStats.library_use || 0;
        const notSpecified = usageStats.not_specified || 0;

        // Patron information
        const topPatronName = patronInfo.most_frequent_patron?.name || "N/A";
        const topPatronRole =
          patronInfo.most_frequent_patron?.patron_type ||
          patronInfo.most_frequent_patron?.role ||
          "N/A";

        return `
          <tr>
            <td>${index + 1}</td>
            <td>${this.escapeHtml(title)}</td>
            <td>${this.escapeHtml(authorNames)}</td>
            <td>${this.escapeHtml(edition)}</td>
            <td>${publicationYear || "-"}</td>
            <td>${this.escapeHtml(publisher)}</td>
            <td>${loanCount || 0}</td>
            <td>${homeUse}</td>
            <td>${libraryUse}</td>
            <td>${notSpecified}</td>
            <td>${uniquePatrons}</td>
            <td>${this.escapeHtml(topPatronName)}</td>
            <td>${this.escapeHtml(topPatronRole)}</td>
            <td>${this.escapeHtml(pid)}</td>
          </tr>
        `;
      })
      .join("");

    // Use the generateExportHTML template
    const htmlContent = generateExportHTML({
      reportTitle,
      generatedDate: `${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
      tableHeaders,
      tableRows,
      footerText:
        "This report shows the top 10 most loaned documents with usage type breakdown and detailed patron information based on circulation statistics.",
      fallbackLogoText: "Library Resource Center",
      showUniversityLogo: true,
      showLrcLogo: true,
    });

    // Create and open the HTML in a new window
    const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank");

    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);

    if (printWindow) {
      printWindow.onload = function () {
        setTimeout(() => {
          printWindow.focus();
        }, 500);
      };
    }
  };

  escapeHtml = (text) => {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };

  renderDateRangePicker() {
    const { fromDate, toDate } = this.state;
    return (
      <>
        <Header as="h3" style={{ marginTop: "20px", color: "#225745" }}>
          Pick range of dates
          <Header.Subheader style={{ color: "black", marginTop: "10px" }}>
            Choose between which dates to search the most loaned documents
          </Header.Subheader>
        </Header>
        <List horizontal>
          <List.Item>
            <List.Content>
              <DatePicker
                maxDate={toDate}
                placeholder="From Date"
                handleDateChange={this.handleFromDateChange}
              />
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Content>
              <DatePicker
                minDate={fromDate}
                placeholder="To Date"
                handleDateChange={this.handleToDateChange}
              />
            </List.Content>
          </List.Item>
        </List>
      </>
    );
  }

  renderHeader = () => {
    const { isExporting } = this.state;

    return (
      <Grid columns={2}>
        <Grid.Column>
          <Header as="h3" style={{ color: "#225745" }}>
            Most Loaned Documents
            <Header.Subheader>{this.subtitle}</Header.Subheader>
          </Header>
        </Grid.Column>
        <Grid.Column textAlign="right">
          <ExportSearchResults
            onExportClick={this.handleExportClick}
            isLoading={isExporting}
            supportedFormats={["csv", "pdf"]}
          />
        </Grid.Column>
      </Grid>
    );
  };

  render() {
    const { data, isLoading, error } = this.props;
    const { fromDate, toDate } = this.state;

    const filteredHits = this.getFilteredHits();
    const hitsWithLinks = filteredHits.map((hit) => {
      hit.metadata.loan_count_url = this.createLoansUrl(hit, fromDate, toDate);
      return hit;
    });

    const panes = [
      {
        menuItem: {
          key: "documents",
          icon: "book",
          content: "Most Loaned Documents",
        },
        render: () => (
          <Tab.Pane>
            <Grid columns={1}>
              <Grid.Column>{this.renderDateRangePicker()}</Grid.Column>
              <Grid.Column>{this.renderHeader()}</Grid.Column>
              <Grid.Column>
                <Loader isLoading={isLoading}>
                  <Error error={error}>
                    <SearchResultsList
                      results={hitsWithLinks}
                      ListEntryElement={DocumentListEntry}
                      renderMiddleColumn={(doc) => (
                        <DocumentStatsWithPatronsAndUsage
                          metadata={doc.metadata}
                        />
                      )}
                    />
                  </Error>
                </Loader>
              </Grid.Column>
            </Grid>
          </Tab.Pane>
        ),
      },
      {
        menuItem: {
          key: "roleReport",
          icon: "users",
          content: "Role-Based Report",
        },
        render: () => (
          <Tab.Pane>
            <RoleBasedBorrowingReport
              data={data}
              onExport={this.handleExportClick}
            />
          </Tab.Pane>
        ),
      },
    ];

    return (
      <InlineErrorBoundary>
        <Tab panes={panes} />
      </InlineErrorBoundary>
    );
  }
}

MostLoanedDocumentsList.propTypes = {
  fetchMostLoanedDocuments: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
};

MostLoanedDocumentsList.defaultProps = {
  isLoading: false,
  error: null,
};
